import React, { useState, useEffect } from 'react';
import {
  ClipboardList, Plus, Edit3, Trash2, Send, Eye, Check,
  ChevronDown, ChevronRight, ArrowLeft, Save, X, Users
} from 'lucide-react';
import api from '../services/api';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'file_upload', label: 'File Upload' },
];

const PRACTICE_AREAS = [
  'family_law', 'criminal', 'personal_injury', 'employment',
  'estate_planning', 'real_estate', 'business', 'immigration', 'general'
];

function QuestionnaireManager() {
  const [view, setView] = useState('templates'); // templates | assignments | edit | responses
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit state
  const [editTemplate, setEditTemplate] = useState(null);
  const [editQuestions, setEditQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState(null);

  // Assign state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ questionnaire_id: '', contact_id: '', case_id: '', due_date: '' });

  // Response view
  const [responseData, setResponseData] = useState(null);

  // Create template state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', practice_area: '', template_name: '' });

  useEffect(() => {
    if (view === 'templates') loadTemplates();
    else if (view === 'assignments') loadAssignments();
  }, [view]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await api.get('/client-portal/admin/questionnaires?include_inactive=true');
      setTemplates(res.data);
    } catch (err) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/client-portal/admin/questionnaires/assignments');
      setAssignments(res.data);
    } catch (err) {
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!createForm.title.trim()) return;
    try {
      await api.post('/client-portal/admin/questionnaires', createForm);
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', practice_area: '', template_name: '' });
      setSuccess('Template created');
      loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create template');
    }
  };

  const handleEditTemplate = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`/client-portal/admin/questionnaires/${id}`);
      setEditTemplate(res.data);
      setEditQuestions(res.data.questions || []);
      setView('edit');
    } catch (err) {
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await api.put(`/client-portal/admin/questionnaires/${editTemplate.id}`, {
        title: editTemplate.title,
        description: editTemplate.description,
        practice_area: editTemplate.practice_area,
      });
      setSuccess('Template saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save template');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this questionnaire template?')) return;
    try {
      await api.delete(`/client-portal/admin/questionnaires/${id}`);
      setSuccess('Template deactivated');
      loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to deactivate');
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion?.question_text?.trim()) return;
    try {
      const res = await api.post(`/client-portal/admin/questionnaires/${editTemplate.id}/questions`, newQuestion);
      setEditQuestions([...editQuestions, { ...newQuestion, id: res.data.id, sort_order: editQuestions.length + 1 }]);
      setNewQuestion(null);
      setSuccess('Question added');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add question');
    }
  };

  const handleUpdateQuestion = async (q) => {
    try {
      await api.put(`/client-portal/admin/questionnaires/questions/${q.id}`, q);
      setSuccess('Question updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update question');
    }
  };

  const handleDeleteQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/client-portal/admin/questionnaires/questions/${qid}`);
      setEditQuestions(editQuestions.filter(q => q.id !== qid));
      setSuccess('Question deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete question');
    }
  };

  const handleAssign = async () => {
    if (!assignForm.questionnaire_id || !assignForm.contact_id || !assignForm.case_id) {
      setError('All fields are required');
      return;
    }
    try {
      await api.post('/client-portal/admin/questionnaires/assign', assignForm);
      setShowAssignModal(false);
      setAssignForm({ questionnaire_id: '', contact_id: '', case_id: '', due_date: '' });
      setSuccess('Questionnaire assigned');
      if (view === 'assignments') loadAssignments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to assign questionnaire');
    }
  };

  const handleViewResponses = async (assignmentId) => {
    try {
      setLoading(true);
      const res = await api.get(`/client-portal/admin/questionnaires/assignments/${assignmentId}/responses`);
      setResponseData(res.data);
      setView('responses');
    } catch (err) {
      setError('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (assignmentId) => {
    try {
      await api.post(`/client-portal/admin/questionnaires/assignments/${assignmentId}/review`);
      setSuccess('Marked as reviewed');
      if (responseData) {
        setResponseData({ ...responseData, reviewed_at: new Date().toISOString() });
      }
      if (view === 'assignments') loadAssignments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to mark reviewed');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      assigned: 'bg-blue-900 text-blue-200',
      in_progress: 'bg-yellow-900 text-yellow-200',
      completed: 'bg-green-900 text-green-200',
      submitted: 'bg-green-900 text-green-200',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-900 text-gray-200'}`}>
        {status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  // ========== TEMPLATES VIEW ==========
  const renderTemplates = () => (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Questionnaire Templates</h2>
          <p className="text-sm text-virgo-teal-400 mt-1">{templates.length} templates</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-500 border border-virgo-teal-700"
          >
            <Send className="w-4 h-4" />
            Assign to Client
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.id} className={`bg-virgo-navy-700 rounded-lg p-4 border ${t.is_active ? 'border-virgo-navy-600' : 'border-red-800 opacity-60'}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-white font-medium">{t.title}</h3>
              {!t.is_active && (
                <span className="text-xs bg-red-900 text-red-200 px-2 py-0.5 rounded-full">Inactive</span>
              )}
            </div>
            {t.description && (
              <p className="text-sm text-virgo-teal-400 mb-3 line-clamp-2">{t.description}</p>
            )}
            <div className="flex items-center gap-4 text-xs text-virgo-teal-500 mb-3">
              {t.practice_area && (
                <span className="bg-virgo-navy-600 px-2 py-1 rounded">{t.practice_area.replace(/_/g, ' ')}</span>
              )}
              <span>{t.question_count} questions</span>
              <span>{t.assignment_count} assigned</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEditTemplate(t.id)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500"
              >
                <Edit3 className="w-3 h-3" /> Edit
              </button>
              <button
                onClick={() => {
                  setAssignForm({ ...assignForm, questionnaire_id: t.id });
                  setShowAssignModal(true);
                }}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500"
              >
                <Send className="w-3 h-3" /> Assign
              </button>
              {t.is_active && (
                <button
                  onClick={() => handleDeactivate(t.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-900/50 text-red-300 rounded hover:bg-red-900"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // ========== ASSIGNMENTS VIEW ==========
  const renderAssignments = () => (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Questionnaire Assignments</h2>
          <p className="text-sm text-virgo-teal-400 mt-1">{assignments.length} assignments</p>
        </div>
      </div>

      <div className="bg-virgo-navy-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-virgo-navy-600">
          <thead className="bg-virgo-navy-600">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Questionnaire</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-virgo-teal-400 uppercase">Progress</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-virgo-teal-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Due Date</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-virgo-teal-400 uppercase">Reviewed</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-virgo-navy-600">
            {assignments.map(a => (
              <tr key={a.id} className="hover:bg-virgo-navy-600">
                <td className="px-4 py-3 text-sm text-white">{a.contact_name || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-virgo-teal-300">{a.questionnaire_title}</td>
                <td className="px-4 py-3 text-center">
                  <span className="text-sm text-white">{a.answered_questions}/{a.total_questions}</span>
                  <div className="w-16 bg-virgo-navy-800 rounded-full h-1.5 mx-auto mt-1">
                    <div
                      className="bg-virgo-teal-500 h-1.5 rounded-full"
                      style={{ width: `${a.total_questions ? (a.answered_questions / a.total_questions * 100) : 0}%` }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-center">{getStatusBadge(a.status)}</td>
                <td className="px-4 py-3 text-sm text-virgo-teal-400">
                  {a.due_date ? new Date(a.due_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  {a.reviewed_at ? (
                    <Check className="w-4 h-4 text-green-400 mx-auto" />
                  ) : (
                    <span className="text-xs text-virgo-teal-500">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => handleViewResponses(a.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    {(a.status === 'completed' || a.status === 'submitted') && !a.reviewed_at && (
                      <button
                        onClick={() => handleMarkReviewed(a.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-900/50 text-green-300 rounded hover:bg-green-900"
                      >
                        <Check className="w-3 h-3" /> Review
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {assignments.length === 0 && (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-virgo-teal-400">
                  No questionnaire assignments yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  // ========== EDIT TEMPLATE VIEW ==========
  const renderEditView = () => (
    <>
      <button
        onClick={() => { setView('templates'); setEditTemplate(null); }}
        className="flex items-center gap-2 text-virgo-teal-400 hover:text-white mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Templates
      </button>

      {editTemplate && (
        <>
          {/* Template Header */}
          <div className="bg-virgo-navy-700 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Title</label>
                <input
                  type="text"
                  value={editTemplate.title}
                  onChange={(e) => setEditTemplate({ ...editTemplate, title: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Practice Area</label>
                <select
                  value={editTemplate.practice_area || ''}
                  onChange={(e) => setEditTemplate({ ...editTemplate, practice_area: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Any</option>
                  {PRACTICE_AREAS.map(pa => (
                    <option key={pa} value={pa}>{pa.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Description</label>
                <textarea
                  value={editTemplate.description || ''}
                  onChange={(e) => setEditTemplate({ ...editTemplate, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={handleSaveTemplate} className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700">
                <Save className="w-4 h-4" /> Save Template
              </button>
            </div>
          </div>

          {/* Questions */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Questions ({editQuestions.length})</h3>
            <button
              onClick={() => setNewQuestion({ question_text: '', question_type: 'text', is_required: true, section: '', help_text: '', options: [] })}
              className="flex items-center gap-2 px-3 py-1.5 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 text-sm"
            >
              <Plus className="w-4 h-4" /> Add Question
            </button>
          </div>

          <div className="space-y-3">
            {editQuestions.map((q, idx) => (
              <QuestionEditor
                key={q.id}
                question={q}
                index={idx}
                onUpdate={(updated) => {
                  setEditQuestions(editQuestions.map(qq => qq.id === q.id ? updated : qq));
                  handleUpdateQuestion(updated);
                }}
                onDelete={() => handleDeleteQuestion(q.id)}
              />
            ))}
          </div>

          {/* New Question Form */}
          {newQuestion && (
            <div className="mt-4 bg-virgo-navy-600 rounded-lg p-4 border border-virgo-teal-600">
              <h4 className="text-sm font-medium text-virgo-teal-200 mb-3">New Question</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                    placeholder="Question text..."
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
                <div>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_type: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  >
                    {QUESTION_TYPES.map(qt => (
                      <option key={qt.value} value={qt.value}>{qt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    type="text"
                    value={newQuestion.section || ''}
                    onChange={(e) => setNewQuestion({ ...newQuestion, section: e.target.value })}
                    placeholder="Section (optional)"
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
                {(newQuestion.question_type === 'select' || newQuestion.question_type === 'multiselect') && (
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      placeholder="Options (comma-separated)"
                      onChange={(e) => setNewQuestion({
                        ...newQuestion,
                        options: e.target.value.split(',').map(o => o.trim()).filter(Boolean)
                      })}
                      className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <input
                    type="text"
                    value={newQuestion.help_text || ''}
                    onChange={(e) => setNewQuestion({ ...newQuestion, help_text: e.target.value })}
                    placeholder="Help text (optional)"
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newQuestion.is_required}
                    onChange={(e) => setNewQuestion({ ...newQuestion, is_required: e.target.checked })}
                    className="rounded bg-virgo-navy-700 border-virgo-teal-700"
                  />
                  <span className="text-sm text-virgo-teal-200">Required</span>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={handleAddQuestion} className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg text-sm hover:bg-virgo-teal-700">
                  Add Question
                </button>
                <button onClick={() => setNewQuestion(null)} className="px-4 py-2 text-virgo-teal-300 hover:text-white text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  // ========== RESPONSES VIEW ==========
  const renderResponses = () => (
    <>
      <button
        onClick={() => { setView('assignments'); setResponseData(null); }}
        className="flex items-center gap-2 text-virgo-teal-400 hover:text-white mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Assignments
      </button>

      {responseData && (
        <>
          <div className="bg-virgo-navy-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">{responseData.questionnaire_title}</h2>
                <p className="text-sm text-virgo-teal-400 mt-1">
                  Client: {responseData.contact_name} | Status: {responseData.status}
                  {responseData.completed_at && ` | Submitted: ${new Date(responseData.completed_at).toLocaleDateString()}`}
                </p>
              </div>
              {!responseData.reviewed_at ? (
                <button
                  onClick={() => handleMarkReviewed(responseData.assignment_id)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600"
                >
                  <Check className="w-4 h-4" /> Mark Reviewed
                </button>
              ) : (
                <span className="flex items-center gap-2 text-green-400 text-sm">
                  <Check className="w-4 h-4" /> Reviewed {new Date(responseData.reviewed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {(() => {
              let currentSection = null;
              return responseData.responses.map((r, idx) => {
                const sectionHeader = r.section && r.section !== currentSection;
                if (r.section) currentSection = r.section;
                return (
                  <React.Fragment key={idx}>
                    {sectionHeader && (
                      <div className="pt-4 pb-2 border-b border-virgo-navy-600">
                        <h3 className="text-lg font-semibold text-virgo-gold-400">{r.section}</h3>
                      </div>
                    )}
                    <div className="bg-virgo-navy-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">
                            {r.question_text}
                            {r.is_required && <span className="text-red-400 ml-1">*</span>}
                          </p>
                          <p className="text-xs text-virgo-teal-500 mt-1">{r.question_type}</p>
                        </div>
                      </div>
                      <div className="mt-2 p-3 bg-virgo-navy-600 rounded">
                        {r.response_text ? (
                          <p className="text-virgo-teal-200 whitespace-pre-wrap">{r.response_text}</p>
                        ) : r.response_data ? (
                          <p className="text-virgo-teal-200">{JSON.stringify(r.response_data)}</p>
                        ) : (
                          <p className="text-virgo-teal-500 italic">No response</p>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-virgo-gold-400">Questionnaire Manager</h1>
        <p className="text-virgo-teal-300 mt-1">Create, assign, and review client questionnaires</p>
      </div>

      {/* Tabs (only show when not in edit/responses) */}
      {(view === 'templates' || view === 'assignments') && (
        <div className="flex gap-4 mb-6 border-b border-virgo-navy-600">
          <button
            onClick={() => setView('templates')}
            className={`px-4 py-2 font-medium transition-colors ${
              view === 'templates'
                ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
                : 'text-virgo-teal-300 hover:text-white'
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setView('assignments')}
            className={`px-4 py-2 font-medium transition-colors ${
              view === 'assignments'
                ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
                : 'text-virgo-teal-300 hover:text-white'
            }`}
          >
            Assignments
          </button>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-300 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {loading && view !== 'edit' ? (
        <div className="flex justify-center items-center h-32">
          <div className="text-virgo-teal-400">Loading...</div>
        </div>
      ) : (
        <>
          {view === 'templates' && renderTemplates()}
          {view === 'assignments' && renderAssignments()}
          {view === 'edit' && renderEditView()}
          {view === 'responses' && renderResponses()}
        </>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">New Questionnaire Template</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Title *</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="e.g., Financial Disclosure"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Practice Area</label>
                <select
                  value={createForm.practice_area}
                  onChange={(e) => setCreateForm({ ...createForm, practice_area: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Any</option>
                  {PRACTICE_AREAS.map(pa => (
                    <option key={pa} value={pa}>{pa.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-virgo-teal-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700"
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Assign Questionnaire</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Template *</label>
                <select
                  value={assignForm.questionnaire_id}
                  onChange={(e) => setAssignForm({ ...assignForm, questionnaire_id: parseInt(e.target.value) || '' })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select template...</option>
                  {templates.filter(t => t.is_active).map(t => (
                    <option key={t.id} value={t.id}>{t.title} ({t.question_count} q)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Contact ID *</label>
                <input
                  type="text"
                  value={assignForm.contact_id}
                  onChange={(e) => setAssignForm({ ...assignForm, contact_id: e.target.value })}
                  placeholder="Contact CUID"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Case ID *</label>
                <input
                  type="text"
                  value={assignForm.case_id}
                  onChange={(e) => setAssignForm({ ...assignForm, case_id: e.target.value })}
                  placeholder="Case CUID"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-200 mb-1">Due Date</label>
                <input
                  type="date"
                  value={assignForm.due_date}
                  onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-virgo-teal-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== QUESTION EDITOR SUB-COMPONENT ==========
function QuestionEditor({ question, index, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(question);

  const handleSave = () => {
    onUpdate(draft);
    setEditing(false);
  };

  return (
    <div className="bg-virgo-navy-700 rounded-lg border border-virgo-navy-600">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-virgo-navy-600 rounded-t-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-virgo-teal-500" /> : <ChevronRight className="w-4 h-4 text-virgo-teal-500" />}
          <span className="text-xs text-virgo-teal-500 w-6">#{index + 1}</span>
          <span className="text-sm text-white">{question.question_text}</span>
          {question.is_required && <span className="text-red-400 text-xs">required</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-virgo-navy-600 text-virgo-teal-400 px-2 py-0.5 rounded">{question.question_type}</span>
          {question.section && <span className="text-xs text-virgo-teal-500">{question.section}</span>}
        </div>
      </div>

      {expanded && (
        <div className="p-3 border-t border-virgo-navy-600">
          {editing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={draft.question_text}
                onChange={(e) => setDraft({ ...draft, question_text: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700 rounded text-white text-sm"
              />
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={draft.question_type}
                  onChange={(e) => setDraft({ ...draft, question_type: e.target.value })}
                  className="px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700 rounded text-white text-sm"
                >
                  {QUESTION_TYPES.map(qt => (
                    <option key={qt.value} value={qt.value}>{qt.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={draft.section || ''}
                  onChange={(e) => setDraft({ ...draft, section: e.target.value })}
                  placeholder="Section"
                  className="px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700 rounded text-white text-sm"
                />
                <input
                  type="number"
                  value={draft.sort_order || 0}
                  onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value) })}
                  placeholder="Order"
                  className="px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700 rounded text-white text-sm"
                />
              </div>
              <input
                type="text"
                value={draft.help_text || ''}
                onChange={(e) => setDraft({ ...draft, help_text: e.target.value })}
                placeholder="Help text"
                className="w-full px-2 py-1 bg-virgo-navy-600 border border-virgo-teal-700 rounded text-white text-sm"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.is_required}
                  onChange={(e) => setDraft({ ...draft, is_required: e.target.checked })}
                />
                <span className="text-sm text-virgo-teal-200">Required</span>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSave} className="px-3 py-1 bg-virgo-teal-600 text-white rounded text-xs">Save</button>
                <button onClick={() => { setEditing(false); setDraft(question); }} className="px-3 py-1 text-virgo-teal-300 text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              {question.help_text && <p className="text-sm text-virgo-teal-400 mb-2">{question.help_text}</p>}
              {question.options && question.options.length > 0 && (
                <div className="mb-2">
                  <span className="text-xs text-virgo-teal-500">Options: </span>
                  {question.options.map((o, i) => (
                    <span key={i} className="text-xs bg-virgo-navy-600 text-virgo-teal-300 px-2 py-0.5 rounded mr-1">{o}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(true)} className="flex items-center gap-1 px-2 py-1 text-xs bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500">
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                <button onClick={onDelete} className="flex items-center gap-1 px-2 py-1 text-xs bg-red-900/50 text-red-300 rounded hover:bg-red-900">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QuestionnaireManager;
